// ── Unidades de tiempo ───────────────────────────────────────────────
/** Un día en milisegundos. Estaba a pelo en cuatro sitios; el eje horario del
 *  cuadro de muelles necesita sus hermanos, y tres literales iguales repartidos
 *  son tres sitios donde equivocarse de cero. */
export const MS_DIA  = 86_400_000
export const MS_HORA = 3_600_000
export const MS_MIN  = 60_000

// ── ISO week number ────────────────────────────────────────────────────────────
export function getISOWeek(d: Date): number {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const day  = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() + 4 - day)
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
  return Math.ceil((((date.getTime() - yearStart.getTime()) / MS_DIA) + 1) / 7)
}

// ── Weekly helpers ─────────────────────────────────────────────────────────────
export function getWeekStart(offset: number): Date {
  const now  = new Date()
  const day  = now.getDay()
  const diff = day === 0 ? -6 : 1 - day
  const mon  = new Date(now)
  mon.setDate(now.getDate() + diff + offset * 7)
  mon.setHours(0, 0, 0, 0)
  return mon
}

export function getWeekDays(offset: number): Date[] {
  const mon = getWeekStart(offset)
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(mon); d.setDate(mon.getDate() + i); return d
  })
}

/** Posición de una barra dentro de la semana, en % del ancho del carril.
 *
 *  OJO — semántica exclusiva de `end`: el fin de semana se toma como el lunes
 *  siguiente a las 00:00 y `end` se compara en crudo, así que una OT que termina
 *  el día D no cubre el día D completo.  `ganttPosRango` (mensual/anual) sí lo
 *  trata como día completo.  La divergencia es conocida y se mantiene a propósito
 *  para no alterar el aspecto de la vista semanal; hay un test que la fija. */
export function ganttPos(
  start: Date | null, end: Date | null, weekStart: Date
): { left: number; width: number } | null {
  const wEnd   = new Date(weekStart); wEnd.setDate(weekStart.getDate() + 7)
  const spanMs = wEnd.getTime() - weekStart.getTime()
  const s = start ? start.getTime() : weekStart.getTime()
  const e = end   ? end.getTime()   : wEnd.getTime()
  if (e < weekStart.getTime() || s > wEnd.getTime()) return null
  const left  = Math.max(0,   (s - weekStart.getTime()) / spanMs) * 100
  const right = Math.min(100, (e - weekStart.getTime()) / spanMs) * 100
  return { left, width: Math.max(0.8, right - left) }
}

export function todayLineLeft(weekStart: Date): number | null {
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const wEnd  = new Date(weekStart); wEnd.setDate(weekStart.getDate() + 7)
  if (today < weekStart || today >= wEnd) return null
  return ((today.getTime() - weekStart.getTime()) / (wEnd.getTime() - weekStart.getTime())) * 100
}

// ── Range spans (columnas del eje) ─────────────────────────────────────────────
/** Un tramo del eje temporal: una semana en la vista mensual, un mes en la anual.
 *
 *  `start`/`end` son los límites reales del tramo (para saber si "hoy" cae dentro,
 *  incluso si el tramo asoma fuera del rango visible); `clampedStart`/`clampedEnd`
 *  son los límites recortados al rango visible, y `days` es su anchura en días
 *  — que se usa directamente como peso `fr` de la columna. */
export interface RangeSpan {
  start: Date
  end: Date
  clampedStart: Date
  clampedEnd: Date
  days: number
  /** Sólo en tramos de semana (vista mensual). */
  isoWeek?: number
  /** Sólo en tramos de mes (vista anual): 0 = enero. */
  monthIndex?: number
}

// ── Monthly helpers ────────────────────────────────────────────────────────────
export function getMonthBounds(offset: number): { monthStart: Date; monthEnd: Date } {
  const now        = new Date()
  const monthStart = new Date(now.getFullYear(), now.getMonth() + offset, 1)
  const monthEnd   = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0)
  monthStart.setHours(0, 0, 0, 0)
  monthEnd.setHours(0, 0, 0, 0)
  return { monthStart, monthEnd }
}

/** Tramos de semana ISO que cubren el mes, recortados a sus bordes. */
export function getMonthWeekSpans(monthStart: Date, monthEnd: Date): RangeSpan[] {
  const firstMonday = new Date(monthStart)
  const dow = firstMonday.getDay()
  firstMonday.setDate(firstMonday.getDate() - (dow === 0 ? 6 : dow - 1))
  firstMonday.setHours(0, 0, 0, 0)

  const weeks: RangeSpan[] = []
  let curr = new Date(firstMonday)

  while (curr <= monthEnd) {
    const weekStart    = new Date(curr)
    const weekEnd      = new Date(curr); weekEnd.setDate(curr.getDate() + 6); weekEnd.setHours(0, 0, 0, 0)
    const clampedStart = weekStart < monthStart ? new Date(monthStart) : new Date(weekStart)
    const clampedEnd   = weekEnd   > monthEnd   ? new Date(monthEnd)   : new Date(weekEnd)
    const days         = Math.round((clampedEnd.getTime() - clampedStart.getTime()) / MS_DIA) + 1
    weeks.push({
      start: weekStart, end: weekEnd, clampedStart, clampedEnd, days,
      isoWeek: getISOWeek(weekStart),
    })
    curr = new Date(curr); curr.setDate(curr.getDate() + 7)
  }
  return weeks
}

// ── Yearly helpers ─────────────────────────────────────────────────────────────
export function getYearBounds(offset: number): { yearStart: Date; yearEnd: Date } {
  const year      = new Date().getFullYear() + offset
  const yearStart = new Date(year, 0, 1)
  const yearEnd   = new Date(year, 11, 31)
  yearStart.setHours(0, 0, 0, 0)
  yearEnd.setHours(0, 0, 0, 0)
  return { yearStart, yearEnd }
}

/** Los 12 tramos de mes del año.  Espejo de `getMonthWeekSpans`: cada mes cae
 *  entero dentro del año, así que no hace falta recorte real, pero se rellenan
 *  `clampedStart`/`clampedEnd` igual para que `RangeSpan` sea uniforme. */
export function getYearMonthSpans(yearStart: Date, yearEnd: Date): RangeSpan[] {
  const year = yearStart.getFullYear()
  return Array.from({ length: 12 }, (_, m) => {
    const start = new Date(year, m, 1);      start.setHours(0, 0, 0, 0)
    const end   = new Date(year, m + 1, 0);  end.setHours(0, 0, 0, 0)
    const clampedStart = start < yearStart ? new Date(yearStart) : new Date(start)
    const clampedEnd   = end   > yearEnd   ? new Date(yearEnd)   : new Date(end)
    const days = Math.round((clampedEnd.getTime() - clampedStart.getTime()) / MS_DIA) + 1
    return { start, end, clampedStart, clampedEnd, days, monthIndex: m }
  })
}

// ── Range positioning (mensual y anual) ────────────────────────────────────────
/** Posición de una barra dentro de un rango arbitrario [rangeStart, rangeEnd], en %.
 *
 *  `rangeEnd` y `end` son inclusivos: se suma un día a ambos para que la barra
 *  cubra el último día completo.  Sirve igual para un mes que para un año. */
export function ganttPosRango(
  start: Date | null, end: Date | null, rangeStart: Date, rangeEnd: Date
): { left: number; width: number } | null {
  const dayAfterEnd = new Date(rangeEnd); dayAfterEnd.setDate(rangeEnd.getDate() + 1)
  const s = start ? start.getTime() : rangeStart.getTime()
  const e = end   ? end.getTime() + MS_DIA : dayAfterEnd.getTime()
  return posEnMs(s, e, rangeStart.getTime(), dayAfterEnd.getTime(), 0.5)
}

export function todayLineLeftRango(rangeStart: Date, rangeEnd: Date): number | null {
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const dayAfterEnd = new Date(rangeEnd); dayAfterEnd.setDate(rangeEnd.getDate() + 1)
  if (today < rangeStart || today >= dayAfterEnd) return null
  const spanMs = dayAfterEnd.getTime() - rangeStart.getTime()
  return ((today.getTime() - rangeStart.getTime()) / spanMs) * 100
}

/** Posiciones en % de las líneas de rejilla internas: una por frontera entre
 *  tramos consecutivos, así que devuelve `spans.length - 1` valores. */
export function boundaryPcts(spans: RangeSpan[], totalDays: number): number[] {
  let cumDays = 0
  return spans.slice(0, -1).map(s => {
    cumDays += s.days
    return (cumDays / totalDays) * 100
  })
}

// ── Posicionamiento en milisegundos (eje horario) ────────────────────────
/** Posición de una barra dentro de un rango arbitrario, TODO en milisegundos.
 *
 *  Es la aritmética desnuda que hay debajo de `ganttPosRango`, sin su suma de un
 *  día: aquí `finMs` es **exclusivo** y no se le añade nada, porque un camión de
 *  08:00 a 09:30 ocupa exactamente noventa minutos y ni un segundo más.
 *
 *  `ganttPosRango` pasa a escribirse encima de esta función para que la
 *  aritmética viva en un solo sitio; lo que impide que se desvíe al hacerlo es
 *  el test de regresión que ya fija su semántica de `fechaFin`.
 *
 *  `anchoMin` es el suelo en % para que una barra cortísima siga siendo visible:
 *  0,5 en los ejes de días, más en los de horas, donde el carril es más ancho. */
export function posEnMs(
  inicioMs: number, finMs: number, rangoIniMs: number, rangoFinMs: number, anchoMin = 0.5,
): { left: number; width: number } | null {
  const spanMs = rangoFinMs - rangoIniMs
  if (spanMs <= 0) return null
  if (finMs <= rangoIniMs || inicioMs >= rangoFinMs) return null
  const left  = Math.max(0,   (Math.max(inicioMs, rangoIniMs) - rangoIniMs) / spanMs) * 100
  const right = Math.min(100, (Math.min(finMs, rangoFinMs)    - rangoIniMs) / spanMs) * 100
  return { left, width: Math.max(anchoMin, right - left) }
}

/** La línea de «ahora» dentro de un rango en milisegundos, al MINUTO.
 *
 *  `todayLineLeft` y `todayLineLeftRango` truncan a medianoche porque su grano es
 *  el día. En un carril de catorce horas eso deja la línea siempre pegada al
 *  borde izquierdo, diciendo que son las seis de la mañana a cualquier hora. */
export function ahoraEnMs(rangoIniMs: number, rangoFinMs: number, ahora = Date.now()): number | null {
  if (ahora < rangoIniMs || ahora >= rangoFinMs) return null
  return ((ahora - rangoIniMs) / (rangoFinMs - rangoIniMs)) * 100
}
